import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, RotateCcw, Calendar, Inbox, Loader2, Home } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * Feb 2026 · 6 Sep 2026 · User-Directive „Reaktivierung-Reverse-Flow".
 * Nachdem Admin die Upgrade-PIN erfolgreich eingegeben hat und der
 * Kunde wieder auf Rolle=customer/status=subscriber ist, pruefen wir
 * ob es Vorgaenge gibt, die frueher mit cancelled_reason=
 * "downgrade_to_guest" storniert wurden. Wenn ja, bieten wir eine
 * One-Click-Wiederherstellung an. Verwendung: Der Aufrufer oeffnet
 * das Modal, klickt „Wiederherstellen" → das Modal ruft das Upgrade
 * NOCHMAL mit restore_cascade=true auf (der Endpoint ist idempotent
 * bezueglich des Rollen-Flips; er macht dann NUR das Restore).
 */
export default function ReactivateRestoreModal({ open, row, onDone, onSkip }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pin, setPin] = useState("");
  const rowId = row?.id;
  const onSkipRef = useRef(onSkip);
  useEffect(() => { onSkipRef.current = onSkip; });

  useEffect(() => {
    if (!open || !rowId) { setPreview(null); setPin(""); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/allowed-emails/${rowId}/upgrade-preview`);
        setPreview(data);
      } catch (e) {
        // Feb 2026 · 6 Sep 2026 · Bug-Fix (iteration_31): Der Backend-Guard
        // fuer role==guest ist entfernt. Falls trotzdem ein Fehler kommt (Netz,
        // 404), erst laut melden, dann skippen — nicht mehr silent-fail.
        toast.error(formatErr(e.response?.data?.detail) || e.message);
        onSkipRef.current?.();
      } finally { setLoading(false); }
    })();
  }, [open, rowId]);

  const bookings = preview?.bookings_restorable ?? 0;
  const requests = preview?.requests_restorable ?? 0;
  // Feb 2026 · 9 Sep 2026 · Objektverwaltung — auch archivierte Objekte werden
  // im Restore-Cascade wiederhergestellt.
  const properties = preview?.properties_restorable ?? 0;
  const nothingToRestore = !loading && preview !== null && bookings === 0 && requests === 0 && properties === 0;

  // Wenn nichts wiederherzustellen ist, gleich schliessen. useEffect MUSS vor
  // frueherem Return stehen, sonst React-Hooks-Rules-Verletzung.
  useEffect(() => {
    if (open && nothingToRestore) {
      onSkipRef.current?.();
    }
  }, [nothingToRestore, open]);

  if (!open || !row) return null;

  // Feb 2026 · 6 Sep 2026 · Bug-Fix (iteration_31): Der Aufruf des
  // upgrade-preview-Endpoints innerhalb dieses Modals muss `role`-agnostisch
  // sein — Modal oeffnet sich NACH dem Upgrade, wenn die Rolle bereits auf
  // 'customer' geflippt ist. Der Backend-Guard wurde entfernt. Falls der
  // Backend-Call trotzdem einen unerwarteten Fehler wirft (Netzwerk, 500),
  // melden wir es JETZT laut statt still zu schweigen. Zusaetzlich: fuer den
  // Restore-Call unten fangen wir nur 403/400 speziell ab.
  const restore = async () => {
    if (!/^\d{4}$/.test(pin)) {
      toast.error("Bitte 4 Ziffern eingeben.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/allowed-emails/${row.id}/upgrade-guest`,
        { admin_pin: pin, restore_cascade: true },
      );
      const c = data.cascade || {};
      const parts = [];
      if (c.bookings_restored) parts.push(`${c.bookings_restored} Buchungen`);
      if (c.requests_restored) parts.push(`${c.requests_restored} Anfragen`);
      if (c.properties_restored) parts.push(`${c.properties_restored} Objekte`);
      toast.success(parts.length
        ? `${parts.join(" · ")} wiederhergestellt.`
        : "Keine Vorgaenge wiederhergestellt.",
      );
      onDone?.(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) toast.error("PIN ist falsch. Bitte erneut eingeben.");
      else if (status === 400 && err.response?.data?.detail?.toLowerCase?.().includes("gast")) {
        toast.info("Konto ist bereits aktiv — keine Wiederherstellung noetig.");
        onSkip?.();
      } else {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
      }
    } finally { setBusy(false); }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(13,25,48,0.6)" }}
      onClick={onSkip}
      data-testid="reactivate-restore-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white w-full max-w-lg relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onSkip}
          className="absolute top-3 right-3 text-navy-dark/50 hover:text-navy"
          aria-label="Schliessen"
          data-testid="reactivate-restore-close"
        >
          <X className="w-4 h-4" strokeWidth={1.6} />
        </button>
        <div className="p-6 md:p-8">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center mb-4"
            style={{ background: withAlpha(BRAND_COLORS.gold, 0.15), color: BRAND_COLORS.goldDark }}
          >
            <RotateCcw className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <h2 className="font-display text-2xl text-navy leading-tight mb-1">
            Alte Vorgaenge wiederherstellen?
          </h2>
          <p className="text-[13px] text-navy-dark/70 leading-relaxed mb-5">
            Beim vorherigen Downgrade auf Gast wurden Vorgaenge dieses
            Kunden automatisch storniert. Jetzt, wo <b>{row.email}</b>
            wieder aktiv ist, koennen sie zurueckgeholt werden.
          </p>

          {loading ? (
            <div className="flex items-center gap-2 text-navy-dark/60 text-[13px] py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.6} />
              Wiederherstellbare Vorgaenge werden gezaehlt ...
            </div>
          ) : (
            <div
              className="border p-4 mb-5"
              data-testid="reactivate-restore-cascade"
              style={{ borderColor: withAlpha(BRAND_COLORS.gold, 0.55), background: withAlpha(BRAND_COLORS.gold, 0.08) }}
            >
              <div className="text-[11px] tracking-[0.18em] uppercase" style={{ color: BRAND_COLORS.goldDark }}>
                Wiederherstellbar
              </div>
              <ul className="space-y-2 mt-2">
                {bookings > 0 && (
                  <li className="flex items-center gap-3 text-[13.5px] text-navy" data-testid="reactivate-restore-bookings">
                    <Calendar className="w-4 h-4 text-navy-dark/60" strokeWidth={1.6} />
                    <span><b className="tabular-nums">{bookings}</b> {bookings === 1 ? "Buchung wird als bestaetigt reaktiviert" : "Buchungen werden als bestaetigt reaktiviert"}</span>
                  </li>
                )}
                {requests > 0 && (
                  <li className="flex items-center gap-3 text-[13.5px] text-navy" data-testid="reactivate-restore-requests">
                    <Inbox className="w-4 h-4 text-navy-dark/60" strokeWidth={1.6} />
                    <span><b className="tabular-nums">{requests}</b> {requests === 1 ? "Anfrage kommt zurueck als neu" : "Anfragen kommen zurueck als neu"}</span>
                  </li>
                )}
                {properties > 0 && (
                  <li className="flex items-center gap-3 text-[13.5px] text-navy" data-testid="reactivate-restore-properties">
                    <Home className="w-4 h-4 text-navy-dark/60" strokeWidth={1.6} />
                    <span><b className="tabular-nums">{properties}</b> {properties === 1 ? 'Objekt wird aus „Entfernte Objekte" zurückgeholt' : 'Objekte werden aus „Entfernte Objekte" zurückgeholt'}</span>
                  </li>
                )}
              </ul>
              <div className="text-[11.5px] text-navy-dark/60 italic mt-3 leading-snug">
                Team schaut anschliessend nochmal drauf und ordnet neu ein.
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 block mb-1.5">
              Admin-PIN (Bestaetigung)
            </label>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); restore(); } }}
              placeholder="4 Ziffern"
              data-testid="reactivate-restore-pin-input"
              className="w-32 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[14px] tabular-nums tracking-widest text-navy"
            />
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onSkip}
              className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-3 py-2"
              data-testid="reactivate-restore-skip"
            >
              Nicht wiederherstellen
            </button>
            <button
              type="button"
              onClick={restore}
              disabled={busy || loading}
              data-testid="reactivate-restore-confirm"
              className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
              style={{ background: BRAND_COLORS.navy }}
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
              Wiederherstellen
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
